import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import NumberLineInterval from "../../../../components/visuals/NumberLineInterval";

/* ===== Helpers ===== */
const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; };
function simplifyFrac(num, den) { if (den < 0) { num = -num; den = -den; } const g = gcd(num, den); return { num: num / g, den: den / g }; }
function rawFracLatex(num, den) { if (den < 0) { num = -num; den = -den; } const s = num < 0 ? "-" : ""; const N = Math.abs(num), D = den; return `${s}\\dfrac{${N}}{${D}}`; }
function mixedLatex(num, den) {
  let { num: N, den: D } = simplifyFrac(num, den);
  const sign = N < 0 ? "-" : "";
  N = Math.abs(N);
  if (N % D === 0) return (sign ? `${sign}${N / D}` : `${N / D}`);
  const A = Math.floor(N / D), R = N % D;
  if (A > 0) return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
  return `${sign}\\dfrac{${R}}{${D}}`;
}
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const coeffX  = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);

// mapy znaków
const SIGN_KINDS = ["lt","gt","le","ge"];                  // <, >, ≤, ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

export function generateTask(generationOptions) {
  const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // A ≠ 0 i A ≠ ±1
  let A = 0;
  while (A === 0 || Math.abs(A) === 1) A = rnd(-9, 9);

  // B ≠ 0 i C ≠ 0  ← NOWE
  let B = 0;
  while (B === 0) B = rnd(-12, 12);

  let C = 0;
  while (C === 0) C = rnd(-12, 12);

  // znak: <, >, ≤, ≥ (jeśli nie wymuszony)
  const kinds = ["lt","gt","le","ge"];
  const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };
  const forceKind = typeof generationOptions === "string"
    ? generationOptions
    : generationOptions?.kind;
  const kind = forceKind ?? kinds[rnd(0, kinds.length - 1)];
  const signLatex = SIGN_LATEX[kind];

  const latex = `${coeffX(A)} ${withSign(B)} \\; ${signLatex} \\; ${C}`;

  // próg t = (C - B) / A
  const t_num = C - B, t_den = A, t_val = t_num / t_den;

  // kierunek po ewentualnym odwróceniu (dla A<0)
  const wantedLess = (kind === "lt" || kind === "le");
  const finalLess  = A > 0 ? wantedLess : !wantedLess;

  // domknięcie nie zmienia się przy odwracaniu
  const inclusive = (kind === "le" || kind === "ge");

  return {
    type: "LinearInequalitySimpleTask",
    data: { A, B, C, kind, latex, t_num, t_den, t_val, finalLess, inclusive }
  };
}


/* ===== Widoki ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <BlockMath math={data.latex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}><InlineMath math={"x\\;\\in"} /></div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 2/3]  lub  [2/3, ∞)"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : (userInput?.S?.raw || userInput?.S || "");
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== Walidacja ===== */
export function checkAnswer({ data }, userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : (userInput?.S?.raw || "");
  if (!raw) return { correct: false };

  const parsed = parseIntervalInput(raw);
  if (!parsed || parsed.kind !== "intervals" || parsed.intervals.length !== 1) return { correct: false };
  const I = parsed.intervals[0];

  const t = data.t_val;
  const tol = 1e-6, eq = (x,y) => Math.abs(x - y) <= tol;

  let ok = false;
  if (data.finalLess) {
    // (-∞, t]  lub  (-∞, t)
    ok = I.left === -Infinity &&
         Number.isFinite(I.right) && eq(I.right, t) &&
         (!!I.rightClosed === !!data.inclusive);
  } else {
    // [t, ∞)  lub  (t, ∞)
    ok = I.right === Infinity &&
         Number.isFinite(I.left) && eq(I.left, t) &&
         (!!I.leftClosed === !!data.inclusive);
  }
  return { correct: !!ok };
}

/* ===== Poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const tL = mixedLatex(data.t_num, data.t_den);
  const left = data.finalLess
    ? "(-\\infty,"
    : (data.inclusive ? `\\left[${tL},` : `\\left(${tL},`); // technicznie tylko do wizualnej symetrii
  // składamy wprost, by nie mieszać nawiasów:
  if (data.finalLess) {
    const rightBracket = data.inclusive ? "]" : ")";
    return <InlineMath math={`x\\in (-\\infty,\\; ${tL}${rightBracket}`} />;
  } else {
    const leftBracket = data.inclusive ? "[" : "(";
    return <InlineMath math={`x\\in ${leftBracket}${tL},\\; \\infty)`} />;
  }
}

/* ===== Wyjaśnienie ===== */
export function renderExplanation({ data }) {
  const { A, B, C, kind, t_num, t_den, t_val, finalLess, inclusive } = data;

  const barMove = B < 0 ? `+\\,${Math.abs(B)}` : `-\\,${Math.abs(B)}`;
  const rhsAfterMove = C - B;
  const barDiv  = A < 0 ? `:\\,(${A})\\;\\text{(odwracamy znak)}` : `:\\,${A}`;

  const rawT = rawFracLatex(t_num, t_den);
  const { num: sN, den: sD } = simplifyFrac(t_num, t_den);
  const needsSimplify = Math.abs(sN) >= sD || gcd(Math.abs(t_num), Math.abs(t_den)) > 1;
  const finalT_Latex = mixedLatex(t_num, t_den);

  const initSign = SIGN_LATEX[kind];
  const finalSign = finalLess ? (inclusive ? "\\le" : "<") : (inclusive ? "\\ge" : ">");

  // wiersze w alignedat – ręczne \; dla naturalnych odstępów
  const L1 = String.raw`${coeffX(A)} ${withSign(B)} &\; ${initSign} &\; ${C} & \quad \big|\, ${barMove}`;
  const L2 = String.raw`${coeffX(A)} &\; ${initSign} &\; ${rhsAfterMove} & \quad \big|\, ${barDiv}`;
  const L3 = String.raw`x &\; ${finalSign} &\; ${rawT} & `;
  const L4 = String.raw`x &\; ${finalSign} &\; ${finalT_Latex} & `;

  const mathAligned = needsSimplify
    ? String.raw`\begin{alignedat}{2}
        ${L1} \\
        ${L2} \\
        ${L3} \\[8pt]
        ${L4}
      \end{alignedat}`
    : String.raw`\begin{alignedat}{2}
        ${L1} \\
        ${L2} \\
        ${L3}
      \end{alignedat}`;

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Rozwiązujemy nierówność (ciąg przekształceń po kreskach).</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={mathAligned} />
      </div>

      <p><strong>Krok 2:</strong> Zaznaczamy rozwiązanie na osi liczbowej.</p>
      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <NumberLineInterval
          t={t_val}
          tLabelLatex={finalT_Latex}
          dir={finalLess ? "left" : "right"}
          closed={inclusive}
          margin={2.5}
        />
      </div>

      <p><strong>Odpowiedź:</strong></p>
      <div style={{ textAlign: "center" }}>
        {finalLess
          ? <BlockMath math={`x\\in \\; (-\\infty,\\; ${finalT_Latex}${inclusive ? "]" : ")"}`} />
          : <BlockMath math={`x\\in \\; ${inclusive ? "[" : "("}${finalT_Latex},\\; \\infty)`} />
        }
      </div>
    </div>
  );
}
